/**
 * Picks an existing client. Shared by the project and contract modals, the
 * support modal, the mail thread's links strip and the call pane.
 *
 * Typing queries `GET /admin/crm/clients?q=` (debounced); picking calls
 * `onPick(id, name)`. Unlike `AriregisterNameInput` it never CREATES a client -
 * everything that uses it needs a client that already exists.
 *
 * The field and the suggestion list are deliberately the same shape as
 * `AriregisterNameInput`'s pill mode: the two sit in the same modals, and two
 * autocompletes that look different in one form read as a bug. Every caller is
 * a CRM-styled screen, so there is no second, bordered look to keep.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Users } from "lucide-react";
import { getClientOptions } from "../../../lib/projects";

export function ClientPicker({
  value,
  selectedId,
  onChange,
  onPick,
  placeholder,
  required,
}: {
  value: string;
  selectedId: string | null;
  onChange: (v: string) => void;
  onPick: (id: string, name: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Suppresses the query that would otherwise fire the moment a name is picked.
  const lastPicked = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 1 || (lastPicked.current !== null && value === lastPicked.current)) {
      setItems([]);
      setOpen(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      getClientOptions(q)
        .then((r) => {
          if (!alive) return;
          setItems(r.rows);
          setOpen(r.rows.length > 0);
        })
        .catch(() => {
          if (alive) {
            setItems([]);
            setOpen(false);
          }
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [value]);

  // A click outside closes the list.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function handleType(v: string) {
    // Typed by hand, so allow a fresh search.
    lastPicked.current = null;
    onChange(v);
  }

  return (
    <div ref={boxRef} className="relative">
      <label className="crm-inp">
        <input
          value={value}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          // A name that was typed but never picked is not a client yet, and the
          // form behind this field saves an id, not a string.
          aria-invalid={required && !selectedId ? true : undefined}
        />
        {searching ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="size-4 shrink-0 text-muted-foreground" />
        )}
      </label>

      {open && items.length > 0 ? (
        <ul className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-3xl bg-[var(--crm-surface)] p-1.5 shadow-[var(--crm-sh-lg)]">
          {items.map((it) => (
            <li key={it.id}>
              <button
                type="button"
                // Keep focus on the field until the click registers.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  lastPicked.current = it.name;
                  setOpen(false);
                  setItems([]);
                  onChange(it.name);
                  onPick(it.id, it.name);
                }}
                className="flex w-full items-center gap-2 rounded-2xl px-3.5 py-2.5 text-left hover:bg-[var(--crm-sunk)]"
              >
                <Users className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="crm-prow-n crm-trunc min-w-0 flex-1">{it.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
